// ── Clock-In shade notification bridge (Android) ────────────────────────────
// Wraps the custom TimerNotificationPlugin (android/app/.../
// TimerNotificationPlugin.java) + TimerNotificationService, which together
// keep a persistent, live-ticking notification showing the running Clock-In
// timer/stopwatch — visible in the notification shade, and on the lock
// screen too (VISIBILITY_PUBLIC; still subject to the user's own Settings >
// Notifications lock-screen preference), without needing the app open. The
// live tick is rendered by Android itself (Notification#setUsesChronometer),
// so it keeps ticking accurately even though JS stops running once the app
// is backgrounded — we only need to call show() again when the *state*
// changes (start/pause/resume/relabel), not on every second.
import { registerPlugin, Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const TimerNotification = registerPlugin('TimerNotification');
const isNative = Capacitor.isNativePlatform();

// Same "ask the first time it's actually needed" pattern as alarmSync.js —
// avoids prompting for notification permission before there's any reason to.
let permissionRequested = false;
async function ensureNotificationPermission() {
  if (permissionRequested) return;
  try {
    const status = await PushNotifications.checkPermissions();
    if (status.receive !== 'granted') await PushNotifications.requestPermissions();
    permissionRequested = true;
  } catch { /* best-effort */ }
}

// mode: "countdown" (whenMs = the epoch ms the timer hits zero) or
// "stopwatch" (whenMs = the epoch ms it started counting up from).
// paused: freezes the display (native formats it) instead of ticking, since
// a notification chronometer can't natively be paused. frozenMs is the
// remaining (countdown) or elapsed (stopwatch) ms at the moment of pause —
// raw, not formatted, so the notification's own Pause/Resume button can
// recompute correctly with zero help from JS (see TimerNotificationService).
export async function showTimerNotification({ mode, label, subText, whenMs, paused = false, frozenMs = 0 }) {
  if (!isNative) return;
  await ensureNotificationPermission();
  try {
    await TimerNotification.show({
      label: label || 'Clock-In',
      subText: subText || undefined,
      whenMs,
      countDown: mode === 'countdown',
      paused,
      frozenMs,
    });
  } catch { /* best-effort — never block the in-app timer on this */ }
}

export async function hideTimerNotification() {
  if (!isNative) return;
  try { await TimerNotification.hide(); } catch { /* best-effort */ }
}

// Picks up a pause/resume triggered from the notification's own button
// (which runs native-only, without JS) so the in-app timer can be brought
// back in sync. Returns null when there's nothing new to apply.
export async function consumePendingTimerToggle() {
  if (!isNative) return null;
  try {
    const result = await TimerNotification.consumePendingToggle();
    return result?.pending ? result : null;
  } catch { return null; }
}

export { isNative as timerNotificationIsNative };
